import { useMemo } from 'react';
import type { FolderTree } from '~/graphql/scalars';
import { assert } from '~/lib/assert';
import { useFolderContext } from './context';

export interface PathEntry {
  id: number;
  name: string;
}

export type FolderPath = readonly PathEntry[];

export const useFolderPath = (folderId: number) => {
  const { folderTree } = useFolderContext();

  return useMemo(() => {
    const toPath = ({ children, ...entry }: FolderTree): FolderPath | null => {
      if (folderId === entry.id) return [entry];
      for (const child of children) {
        const path = toPath(child);
        if (path != null) return [entry, ...path];
      }
      return null;
    };
    const path = toPath(folderTree);

    assert(path != null, `useFolderPath: folderId (${folderId}) not in tree`);

    return path;
  }, [folderId, folderTree]);
};

export const useFolderCopyPaths = (folderId: number) => {
  const { folderTree } = useFolderContext();

  return useMemo(() => {
    const toPaths = (tree: FolderTree, without?: number) => {
      const helper = ({ children, id, name }: FolderTree): PathEntry[] =>
        id === without
          ? []
          : [
              { id, name },
              ...children.flatMap((child) => helper({ ...child, name: `${name}/${child.name}` })),
            ];
      return [{ id: tree.id, name: '(Root Folder)' }, ...tree.children.flatMap(helper)];
    };

    return toPaths(folderTree, folderId);
  }, [folderId, folderTree]);
};
